import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/i18n';
import { getdb } from '@/services/productsService';
import {
  SearchDropdownSettings,
  SearchSectionKey,
  SearchSuggestion,
  SearchSynonym,
  TrendingBlacklistRow,
  TrendingPreviewRow,
  deleteSearchSuggestion,
  deleteSearchSynonym,
  getSearchDropdownSettings,
  hideTrendingTerm,
  listSearchSuggestions,
  listSearchSynonyms,
  listTrendingBlacklist,
  listTrendingPreview,
  resetTrendingBlacklist,
  unhideTrendingTerm,
  updateSearchDropdownSettings,
  upsertSearchSuggestion,
  upsertSearchSynonym,
} from '@/services/searchSuggestionsService';

type ProductOption = { id: string; name: string; brand: string; image: string };

const SECTION_LABELS: Record<SearchSectionKey, string> = {
  recent: 'Recent',
  trending: 'Trending searches',
  recommend: 'ALI Recommend',
  promoted: 'Promoted Item',
};

const SearchSuggestionsPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [synonyms, setSynonyms] = useState<SearchSynonym[]>([]);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [settings, setSettings] = useState<SearchDropdownSettings | null>(null);
  const [alias, setAlias] = useState('');
  const [expansion, setExpansion] = useState('');
  const [recommendLabel, setRecommendLabel] = useState('');
  const [recommendProductId, setRecommendProductId] = useState('');
  const [recommendProductQuery, setRecommendProductQuery] = useState('');
  const [promotedLabel, setPromotedLabel] = useState('');
  const [promotedProductId, setPromotedProductId] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [recommendOptions, setRecommendOptions] = useState<ProductOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [trendingPreview, setTrendingPreview] = useState<TrendingPreviewRow[]>([]);
  const [trendingBlacklist, setTrendingBlacklist] = useState<TrendingBlacklistRow[]>([]);

  const recommendRows = useMemo(
    () => suggestions.filter((s) => s.section === 'recommend'),
    [suggestions],
  );
  const promotedRows = useMemo(
    () => suggestions.filter((s) => s.section === 'promoted'),
    [suggestions],
  );
  const liveTrending = useMemo(
    () => trendingPreview.filter((row) => !row.isBlacklisted),
    [trendingPreview],
  );
  const hiddenTrending = useMemo(() => {
    const byQuery = new Map<string, TrendingPreviewRow>();
    trendingPreview.forEach((row) => {
      if (row.isBlacklisted) byQuery.set(row.query, row);
    });
    trendingBlacklist.forEach((row) => {
      if (!byQuery.has(row.query)) {
        byQuery.set(row.query, {
          query: row.query,
          eventCount: 0,
          sessionCount: 0,
          isBlacklisted: true,
        });
      }
    });
    return Array.from(byQuery.values()).sort((a, b) => a.query.localeCompare(b.query));
  }, [trendingPreview, trendingBlacklist]);

  const orderedSections = useMemo(() => {
    if (!settings) return [];
    return (Object.keys(settings.sections) as SearchSectionKey[])
      .map((key) => ({ key, ...settings.sections[key] }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
  }, [settings]);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [synonymRows, suggestionRows, dropdownSettings, trendingRows, blacklistRows] =
        await Promise.all([
          listSearchSynonyms(),
          listSearchSuggestions(),
          getSearchDropdownSettings(),
          listTrendingPreview(),
          listTrendingBlacklist(),
        ]);
      setSynonyms(synonymRows);
      setSuggestions(suggestionRows);
      setSettings(dropdownSettings);
      setTrendingPreview(trendingRows);
      setTrendingBlacklist(blacklistRows);
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const q = productQuery.trim();
    if (q.length < 2) {
      setProductOptions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await getdb(1, 8, { searchTerm: q });
        if (cancelled) return;
        setProductOptions(
          (data || []).map((item: any) => ({
            id: String(item.id || item._id || ''),
            name: item.name || '',
            brand: item.brand || '',
            image: item.image || '',
          })).filter((p) => p.id),
        );
      } catch {
        if (!cancelled) setProductOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [productQuery]);

  useEffect(() => {
    const q = recommendProductQuery.trim();
    if (q.length < 2) {
      setRecommendOptions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await getdb(1, 8, { searchTerm: q });
        if (cancelled) return;
        setRecommendOptions(
          (data || []).map((item: any) => ({
            id: String(item.id || item._id || ''),
            name: item.name || '',
            brand: item.brand || '',
            image: item.image || '',
          })).filter((p) => p.id),
        );
      } catch {
        if (!cancelled) setRecommendOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [recommendProductQuery]);

  const persistSettings = async (next: SearchDropdownSettings) => {
    try {
      setSaving(true);
      const saved = await updateSearchDropdownSettings({
        dropdownEnabled: next.dropdownEnabled,
        sections: next.sections,
      });
      setSettings(saved);
      toast.success(t('searchCurateSettingsSaved'));
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
      await loadAll();
    } finally {
      setSaving(false);
    }
  };

  const moveSection = async (key: SearchSectionKey, direction: -1 | 1) => {
    if (!settings) return;
    const ordered = [...orderedSections];
    const index = ordered.findIndex((s) => s.key === key);
    const swapWith = index + direction;
    if (index < 0 || swapWith < 0 || swapWith >= ordered.length) return;
    const nextKeys = ordered.map((s) => s.key);
    const tmp = nextKeys[index];
    nextKeys[index] = nextKeys[swapWith];
    nextKeys[swapWith] = tmp;
    const nextSections = { ...settings.sections };
    nextKeys.forEach((sectionKey, sortOrder) => {
      nextSections[sectionKey] = {
        ...nextSections[sectionKey],
        sortOrder,
      };
    });
    const next = { ...settings, sections: nextSections };
    setSettings(next);
    await persistSettings(next);
  };

  const toggleSection = async (key: SearchSectionKey, enabled: boolean) => {
    if (!settings) return;
    const next = {
      ...settings,
      sections: {
        ...settings.sections,
        [key]: { ...settings.sections[key], enabled },
      },
    };
    setSettings(next);
    await persistSettings(next);
  };

  const toggleDropdown = async (enabled: boolean) => {
    if (!settings) return;
    const next = { ...settings, dropdownEnabled: enabled };
    setSettings(next);
    await persistSettings(next);
  };

  const handleAddSynonym = async () => {
    try {
      setSaving(true);
      await upsertSearchSynonym({ alias, expansion, enabled: true });
      setAlias('');
      setExpansion('');
      toast.success(t('searchCurateSynonymSaved'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSynonym = async (row: SearchSynonym) => {
    try {
      await upsertSearchSynonym({
        alias: row.alias,
        expansion: row.expansion,
        enabled: !row.enabled,
      });
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    }
  };

  const handleDeleteSynonym = async (row: SearchSynonym) => {
    try {
      await deleteSearchSynonym(row.alias);
      toast.success(t('searchCurateDeleted'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    }
  };

  const handleAddRecommendProduct = async () => {
    try {
      setSaving(true);
      await upsertSearchSuggestion({
        section: 'recommend',
        label: recommendLabel,
        productId: recommendProductId,
        query: recommendLabel,
        sortOrder: recommendRows.length,
        enabled: true,
      });
      setRecommendLabel('');
      setRecommendProductId('');
      setRecommendProductQuery('');
      toast.success(t('searchCurateRecommendSaved'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddPromoted = async () => {
    try {
      setSaving(true);
      await upsertSearchSuggestion({
        section: 'promoted',
        label: promotedLabel,
        productId: promotedProductId,
        query: promotedLabel,
        sortOrder: promotedRows.length,
        enabled: true,
      });
      setPromotedLabel('');
      setPromotedProductId('');
      setProductQuery('');
      toast.success(t('searchCuratePromotedSaved'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSuggestion = async (row: SearchSuggestion) => {
    try {
      await deleteSearchSuggestion(row.id);
      toast.success(t('searchCurateDeleted'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    }
  };

  const handleHideTrending = async (query: string) => {
    try {
      setSaving(true);
      await hideTrendingTerm(query);
      toast.success(t('searchCurateTrendingHiddenDone'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnhideTrending = async (query: string) => {
    try {
      setSaving(true);
      await unhideTrendingTerm(query);
      toast.success(t('searchCurateTrendingUnhiddenDone'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetTrending = async () => {
    try {
      setSaving(true);
      await resetTrendingBlacklist();
      toast.success(t('searchCurateTrendingResetDone'));
      await loadAll();
    } catch (error: any) {
      toast.error(error?.message || t('searchCurateSaveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('searchCurateTitle')}</h1>
          <p className="text-muted-foreground">{t('searchCurateDescription')}</p>
        </div>
        <Button variant="outline" onClick={loadAll} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('searchCurateRefresh')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('searchCurateDropdownSettings')}</CardTitle>
          <CardDescription>{t('searchCurateDropdownSettingsDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading || !settings ? (
            <Skeleton className="h-28 w-full" />
          ) : (
            <>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <div className="font-medium">{t('searchCurateDropdownEnabled')}</div>
                  <div className="text-sm text-muted-foreground">{t('searchCurateDropdownEnabledHint')}</div>
                </div>
                <Switch
                  checked={settings.dropdownEnabled}
                  onCheckedChange={toggleDropdown}
                  disabled={saving}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('searchCurateSectionOrder')}</Label>
                <p className="text-sm text-muted-foreground">{t('searchCurateSectionOrderHint')}</p>
                <div className="space-y-2">
                  {orderedSections.map((section, index) => (
                    <div
                      key={section.key}
                      className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="font-medium">{SECTION_LABELS[section.key]}</div>
                        <div className="text-xs text-muted-foreground">
                          {t('searchCurateSortOrder')}: {section.sortOrder}
                          {section.key !== 'trending' && section.key !== 'recent'
                            ? ` · ${t('searchCuratePoolHint')}`
                            : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={section.enabled}
                          onCheckedChange={(enabled) => toggleSection(section.key, enabled)}
                          disabled={saving}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={saving || index === 0}
                          onClick={() => moveSection(section.key, -1)}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={saving || index === orderedSections.length - 1}
                          onClick={() => moveSection(section.key, 1)}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>{t('searchCurateTrendingTitle')}</CardTitle>
            <CardDescription>{t('searchCurateTrendingDescription')}</CardDescription>
          </div>
          <Button variant="outline" onClick={handleResetTrending} disabled={saving || loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('searchCurateTrendingReset')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('searchCurateTrendingNote')}</p>
          <p className="text-xs text-muted-foreground">{t('searchCurateTrendingResetHint')}</p>

          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('searchCurateTrendingLive')}</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('searchCurateQuery')}</TableHead>
                      <TableHead>{t('searchCurateTrendingCounts')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {liveTrending.map((row) => (
                      <TableRow key={`live-${row.query}`}>
                        <TableCell className="font-medium">{row.query}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.eventCount} / {row.sessionCount}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={() => handleHideTrending(row.query)}
                          >
                            {t('searchCurateTrendingHide')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!liveTrending.length && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-muted-foreground">
                          {t('searchCurateTrendingEmptyLive')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-2">
                <Label>{t('searchCurateTrendingHidden')}</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('searchCurateQuery')}</TableHead>
                      <TableHead>{t('searchCurateTrendingCounts')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hiddenTrending.map((row) => (
                      <TableRow key={`hidden-${row.query}`}>
                        <TableCell className="font-medium">{row.query}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.eventCount} / {row.sessionCount}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={() => handleUnhideTrending(row.query)}
                          >
                            {t('searchCurateTrendingUnhide')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!hiddenTrending.length && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-muted-foreground">
                          {t('searchCurateTrendingEmptyHidden')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="synonyms" className="space-y-4">
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="synonyms">{t('searchCurateSynonyms')}</TabsTrigger>
          <TabsTrigger value="recommend">{t('searchCurateRecommend')}</TabsTrigger>
          <TabsTrigger value="promoted">{t('searchCuratePromoted')}</TabsTrigger>
        </TabsList>

        <TabsContent value="synonyms" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('searchCurateAddSynonym')}</CardTitle>
              <CardDescription>{t('searchCurateSynonymHint')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-2">
                <Label>{t('searchCurateAlias')}</Label>
                <Input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="bk" />
              </div>
              <div className="space-y-2">
                <Label>{t('searchCurateExpansion')}</Label>
                <Input value={expansion} onChange={(e) => setExpansion(e.target.value)} placeholder="birkenstock" />
              </div>
              <div className="flex items-end">
                <Button onClick={handleAddSynonym} disabled={saving || !alias.trim() || !expansion.trim()}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('searchCurateAdd')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('searchCurateAlias')}</TableHead>
                      <TableHead>{t('searchCurateExpansion')}</TableHead>
                      <TableHead>{t('searchCurateEnabled')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {synonyms.map((row) => (
                      <TableRow key={row.alias}>
                        <TableCell className="font-medium">{row.alias}</TableCell>
                        <TableCell>{row.expansion}</TableCell>
                        <TableCell>
                          <Switch checked={row.enabled} onCheckedChange={() => handleToggleSynonym(row)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteSynonym(row)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!synonyms.length && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-muted-foreground">
                          {t('searchCurateEmpty')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommend" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('searchCurateAddRecommend')}</CardTitle>
              <CardDescription>{t('searchCurateRecommendPoolHint')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('labelName')}</Label>
                  <Input value={recommendLabel} onChange={(e) => setRecommendLabel(e.target.value)} />
                </div>
                <div className="space-y-2 relative">
                  <Label>{t('searchCurateProductSearch')}</Label>
                  <Input
                    value={recommendProductQuery}
                    onChange={(e) => setRecommendProductQuery(e.target.value)}
                    placeholder={t('searchItems')}
                  />
                  {recommendOptions.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border bg-background shadow-md max-h-48 overflow-y-auto">
                      {recommendOptions.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent"
                          onClick={() => {
                            setRecommendProductId(p.id);
                            if (!recommendLabel) setRecommendLabel(p.name || p.brand || p.id);
                            setRecommendProductQuery(`${p.name} (${p.id.slice(0, 8)})`);
                            setRecommendOptions([]);
                          }}
                        >
                          {p.image ? <img src={p.image} alt="" className="h-7 w-7 rounded object-cover" /> : null}
                          <span className="truncate text-sm">{p.name || p.brand}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {t('searchCurateSelectedProduct')}: {recommendProductId || '—'}
              </div>
              <Button
                onClick={handleAddRecommendProduct}
                disabled={saving || !recommendLabel.trim() || !recommendProductId.trim()}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('searchCurateAdd')}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('labelName')}</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recommendRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell className="font-mono text-xs">{row.productId || row.query}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteSuggestion(row)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!recommendRows.length && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground">{t('searchCurateEmpty')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promoted" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('searchCurateAddPromoted')}</CardTitle>
              <CardDescription>{t('searchCuratePromotedPoolHint')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('labelName')}</Label>
                  <Input value={promotedLabel} onChange={(e) => setPromotedLabel(e.target.value)} />
                </div>
                <div className="space-y-2 relative">
                  <Label>{t('searchCurateProductSearch')}</Label>
                  <Input
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    placeholder={t('searchItems')}
                  />
                  {productOptions.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border bg-background shadow-md max-h-48 overflow-y-auto">
                      {productOptions.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent"
                          onClick={() => {
                            setPromotedProductId(p.id);
                            if (!promotedLabel) setPromotedLabel(p.name || p.brand || p.id);
                            setProductQuery(`${p.name} (${p.id.slice(0, 8)})`);
                            setProductOptions([]);
                          }}
                        >
                          {p.image ? <img src={p.image} alt="" className="h-7 w-7 rounded object-cover" /> : null}
                          <span className="truncate text-sm">{p.name || p.brand}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {t('searchCurateSelectedProduct')}: {promotedProductId || '—'}
              </div>
              <Button onClick={handleAddPromoted} disabled={saving || !promotedLabel.trim() || !promotedProductId.trim()}>
                <Plus className="mr-2 h-4 w-4" />
                {t('searchCurateAdd')}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('labelName')}</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promotedRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell className="font-mono text-xs">{row.productId}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteSuggestion(row)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!promotedRows.length && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground">{t('searchCurateEmpty')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SearchSuggestionsPage;
